/**
 * React hook for screen recording functionality
 */

import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { screenRecorder, type RecordingOptions } from '@/lib/recording'
import { mouseTracker } from '@/lib/mouse-tracker'
import { useRecordingStore } from '@/store/recording-store'
import { useEditorStore } from '@/store/editor-store'

export function useRecorder() {
    const navigate = useNavigate()
    const timerRef = useRef<number | null>(null)

    const {
        status,
        duration,
        setStatus,
        incrementDuration,
        setBlob,
        setMouseData,
        setOriginalFrameRate,
        setSourceTitle,
        reset,
    } = useRecordingStore()

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
        }
    }, [])

    /**
     * Start recording with the given options
     */
    const startRecording = useCallback(async (options: RecordingOptions) => {
        try {
            useEditorStore.getState().reset()
            reset()

            // Request permissions and get stream
            const stream = await screenRecorder.requestPermissions(options)

            // Capture screen dimensions and tab/window title from the video track
            const videoTrack = stream.getVideoTracks()[0]
            if (videoTrack) {
                const settings = videoTrack.getSettings() as any
                if (settings.width && settings.height) {
                    useRecordingStore.getState().setScreenDimensions(settings.width, settings.height)
                }
                // Extract a clean title from the track label (e.g. "Tab: My Page - Chrome" → "My Page")
                const label = videoTrack.label
                if (label) {
                    const clean = label
                        .replace(/^(Tab|Window|Screen):\s*/i, '')
                        .replace(/\s*[-–]\s*(Google Chrome|Mozilla Firefox|Microsoft Edge|Safari)$/i, '')
                        .trim()
                    setSourceTitle(clean || null)
                }
            }

            // Start mouse tracking
            const { screenWidth, screenHeight } = useRecordingStore.getState()
            mouseTracker.start(screenWidth, screenHeight)

            // Start recording
            screenRecorder.startRecording()

            // Update state
            setStatus('recording')

            // Start duration timer
            timerRef.current = window.setInterval(() => {
                incrementDuration()
            }, 1000)

        } catch (error) {
            console.error('Failed to start recording:', error)
            screenRecorder.stopAllStreams() // Kill any dangling streams from requestPermissions
            reset()
            throw error
        }
    }, [reset, setStatus, incrementDuration])

    /**
     * Pause the current recording
     */
    const pauseRecording = useCallback(() => {
        screenRecorder.pauseRecording()
        setStatus('paused')

        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
    }, [setStatus])

    /**
     * Resume a paused recording
     */
    const resumeRecording = useCallback(() => {
        screenRecorder.resumeRecording()
        setStatus('recording')

        timerRef.current = window.setInterval(() => {
            incrementDuration()
        }, 1000)
    }, [setStatus, incrementDuration])

    /**
     * Stop recording and navigate to editor
     */
    const stopRecording = useCallback(async () => {
        try {
            // Stop timer
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }

            // Stop mouse tracking (Web app cannot capture system-wide cursor data)
            mouseTracker.stop()
            setMouseData([])

            // Stop recording
            const result = await screenRecorder.stopRecording()
            setBlob(result.blob)
            if (result.cameraBlob) {
                useRecordingStore.getState().setCameraBlob(result.cameraBlob)
            }
            setOriginalFrameRate(result.frameRate)

            // Initialize editor with recording data
            const editorStore = useEditorStore.getState()
            editorStore.setDuration(result.duration)
            editorStore.initClips(result.duration)
            editorStore.updateCursorSettings({ visible: false }) // Web/extension recording has baked cursor, hide custom overlay by default!

            // Update state
            setStatus('stopped')

            // Focus the app window before navigating (important for cross-tab recording)
            window.focus()

            // Navigate to editor
            navigate('/editor')

        } catch (error) {
            console.error('Failed to stop recording:', error)
            throw error
        }
    }, [setBlob, setMouseData, setStatus, navigate])

    /**
     * Cancel recording without saving
     */
    const cancelRecording = useCallback(async () => {
        try {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }

            mouseTracker.stop()
            await screenRecorder.stopRecording()
            reset()

        } catch (error) {
            console.error('Failed to cancel recording:', error)
            reset()
        }
    }, [reset])

    const toggleMic = useCallback((enabled: boolean) => {
        screenRecorder.toggleMic(enabled)
    }, [])

    return {
        status,
        duration,
        isRecording: status === 'recording',
        isPaused: status === 'paused',
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        cancelRecording,
        toggleMic,
    }
}
